/* eslint-disable react/prop-types */
/* eslint-disable no-param-reassign */

import React, { useEffect, useState, useRef } from 'react';
import { useLanguage } from '@components/common/locale/useLanguage';
import { _ } from '@evershop/evershop/src/lib/locale/translate';
import ProductPicks from './components/productPicks/ProductPicks';
import { useProductModal } from './components/productModal/useProductModal';
import ProductModal from './components/productModal/ProductModal';
import { extractDescriptionText } from './utils/categoryHelpers';

// Shuffle array function
var shuffleArray = array => {
  var shuffled = [...array];
  for (var i = shuffled.length - 1; i > 0; i -= 1) {
    var j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};
export default function RandomizedProductPicks(_ref) {
  var {
    collections,
    shoppingCartTitle,
    storeStatus
  } = _ref;
  var {
    isAr
  } = useLanguage();
  var [products, setProducts] = useState([]);
  var [currentIndex, setCurrentIndex] = useState(0);
  var scrollContainerRef = useRef(null);

  /* ── Modal state ── */
  var {
    isOpen,
    product: modalProduct,
    openModal,
    closeModal
  } = useProductModal();

  // Update products data
  useEffect(() => {
    var requiredCode = shoppingCartTitle ? 'menuc-01' : 'menuf-01';
    var collection = collections === null || collections === void 0 ? void 0 : collections.find(col => col.code === requiredCode);
    if (shoppingCartTitle) {
      var _collection$products;
      // For "YOU MIGHT LIKE THESE!" section - show 10 products
      if ((collection === null || collection === void 0 || (_collection$products = collection.products) === null || _collection$products === void 0 || (_collection$products = _collection$products.items) === null || _collection$products === void 0 ? void 0 : _collection$products.length) > 10) {
        setProducts(shuffleArray(collection.products.items).slice(0, 10));
      } else {
        var _collection$products2;
        setProducts((collection === null || collection === void 0 || (_collection$products2 = collection.products) === null || _collection$products2 === void 0 ? void 0 : _collection$products2.items) || []);
      }
    } else {
      var _collection$products3;
      // For "ALL-TIME FAVORITES" section - show 3 products
      // eslint-disable-next-line no-lonely-if
      if ((collection === null || collection === void 0 || (_collection$products3 = collection.products) === null || _collection$products3 === void 0 || (_collection$products3 = _collection$products3.items) === null || _collection$products3 === void 0 ? void 0 : _collection$products3.length) <= 3) {
        var _collection$products4;
        setProducts((collection === null || collection === void 0 || (_collection$products4 = collection.products) === null || _collection$products4 === void 0 ? void 0 : _collection$products4.items) || []);
      } else if (collection) {
        setProducts(shuffleArray(collection.products.items).slice(0, 3));
      }
    }
  }, [collections, shoppingCartTitle]);
  var handleButtonClick = product => {
    var _product$image, _product$image2, _product$price;
    if (storeStatus === '0') return;
    openModal({
      productId: product.productId,
      image: ((_product$image = product.image) === null || _product$image === void 0 ? void 0 : _product$image.single) || ((_product$image2 = product.image) === null || _product$image2 === void 0 ? void 0 : _product$image2.url) || '',
      name: isAr ? product.nameAr || product.name : product.name,
      description: extractDescriptionText(isAr ? product.descriptionAr || product.description : product.description),
      price: Number((_product$price = product.price) === null || _product$price === void 0 || (_product$price = _product$price.regular) === null || _product$price === void 0 ? void 0 : _product$price.value) || 0,
      currency: 'EGP',
      url: product.url || "/product/".concat(product.url_key)
    });
  };

  // Carousel navigation functions
  var scrollToIndex = index => {
    if (scrollContainerRef.current) {
      var container = scrollContainerRef.current;
      var cardWidth = container.scrollWidth / products.length;
      container.scrollTo({
        left: isAr ? -cardWidth * index : cardWidth * index,
        behavior: 'smooth'
      });
      setCurrentIndex(index);
    }
  };
  var handlePrevious = () => {
    var newIndex = Math.max(0, currentIndex - 3);
    scrollToIndex(newIndex);
  };
  var handleNext = () => {
    var maxIndex = Math.max(0, products.length - 3);
    var newIndex = Math.min(maxIndex, currentIndex + 3);
    scrollToIndex(newIndex);
  };
  var canGoPrevious = currentIndex > 0;
  var canGoNext = currentIndex < products.length - 3;
  return (products === null || products === void 0 ? void 0 : products.length) > 0 && /*#__PURE__*/React.createElement("div", {
    className: "random-picks-container md:block hidden"
  }, /*#__PURE__*/React.createElement("div", {
    className: "self-stretch  gap-2 ".concat(shoppingCartTitle ? 'mb-2' : 'mb-[7.5rem]')
  }, /*#__PURE__*/React.createElement("div", {
    className: ""
  }, shoppingCartTitle ? /*#__PURE__*/React.createElement("div", {
    className: "category-header flex items-start justify-start gap-4"
  }, /*#__PURE__*/React.createElement("h1", {
    className: "text-left font-rubik uppercase text-[#C60910] text-[3rem] mt-2"
  }, _('Items to Pair With'), "! \uD83E\uDD29")) : /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-4 mb-8 max-w-[109rem] mx-auto relative ltr:mr-auto rtl:ml-auto"
  }, /*#__PURE__*/React.createElement("div", {
    className: "h-[0.8rem] bg-primary w-20 flex-shrink-0"
  }), /*#__PURE__*/React.createElement("h3", {
    className: "text-[#0E0F0B] font-zoom-pro-compact font-[500] uppercase text-[1.8rem] whitespace-nowrap"
  }, _('ALL-TIME FAVORITES')), /*#__PURE__*/React.createElement("div", {
    className: "h-[0.8rem] flex-grow relative overflow-visible"
  }, /*#__PURE__*/React.createElement("div", {
    className: "absolute top-0 ltr:left-0 rtl:right-0 h-full bg-primary w-[100vw]"
  }))))), shoppingCartTitle && products.length > 3 ?
  /*#__PURE__*/
  // Carousel view for "YOU MIGHT LIKE THESE!"
  React.createElement("div", {
    className: "w-full"
  }, /*#__PURE__*/React.createElement("div", {
    className: "overflow-hidden w-full max-w-[96.5rem] mx-auto mb-8"
  }, /*#__PURE__*/React.createElement("div", {
    ref: scrollContainerRef,
    dir: isAr ? 'rtl' : 'ltr',
    className: "flex overflow-x-hidden scroll-smooth gap-[4.5rem] pt-[6rem] pb-[6rem]",
    style: {
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }
  }, products.map(product => {
    var _product$customizatio, _product$image3, _product$category, _product$category2, _product$category3, _product$price2;
    var isCustomizable = !!((_product$customizatio = product.customizationGroups) !== null && _product$customizatio !== void 0 && _product$customizatio.length);
    return /*#__PURE__*/React.createElement("div", {
      key: product.productId,
      className: "flex-shrink-0 w-[29rem]"
    }, /*#__PURE__*/React.createElement(ProductPicks, {
      storeStatus: storeStatus,
      image: ((_product$image3 = product.image) === null || _product$image3 === void 0 ? void 0 : _product$image3.single) || '',
      category: isAr ? ((_product$category = product.category) === null || _product$category === void 0 ? void 0 : _product$category.nameAr) || ((_product$category2 = product.category) === null || _product$category2 === void 0 ? void 0 : _product$category2.name) : (_product$category3 = product.category) === null || _product$category3 === void 0 ? void 0 : _product$category3.name,
      name: isAr ? product.nameAr || product.name : product.name,
      isSpicy: product.isSpicy,
      isNewItem: product === null || product === void 0 ? void 0 : product.new_item,
      price: ((_product$price2 = product.price) === null || _product$price2 === void 0 || (_product$price2 = _product$price2.regular) === null || _product$price2 === void 0 ? void 0 : _product$price2.value) || '',
      url: product.url || "/product/".concat(product.url_key),
      stockAvailability: product.stock_status !== 'OUT_OF_STOCK',
      onAddToCart: () => handleButtonClick(product),
      shoppingCartTitle: shoppingCartTitle,
      description: extractDescriptionText(isAr ? product.descriptionAr || product.description : product.description),
      isCustomizable: isCustomizable
    }));
  }))), /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-center gap-6 mt-[2.5rem]"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: handlePrevious,
    disabled: !canGoPrevious,
    className: "flex items-center justify-center transition-all rtl:rotate-180 ".concat(canGoPrevious ? 'opacity-100 cursor-pointer hover:scale-110' : 'opacity-40 cursor-not-allowed'),
    "aria-label": "Previous products"
  }, /*#__PURE__*/React.createElement("svg", {
    className: " w-[1.5rem] h-[1.5rem] ",
    viewBox: "0 0 20 30",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M18 2L5 15L18 28",
    stroke: canGoPrevious ? '#C60910' : '#858585',
    strokeWidth: "3",
    strokeLinecap: "round"
  }))), /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: handleNext,
    disabled: !canGoNext,
    className: "flex items-center justify-center transition-all rtl:rotate-180 ".concat(canGoNext ? 'opacity-100 cursor-pointer hover:scale-110' : 'opacity-40 cursor-not-allowed'),
    "aria-label": "Next products"
  }, /*#__PURE__*/React.createElement("svg", {
    className: " w-[1.5rem] h-[1.5rem] ",
    viewBox: "0 0 20 30",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M2 2L15 15L2 28",
    stroke: canGoNext ? '#C60910' : '#858585',
    strokeWidth: "3",
    strokeLinecap: "round"
  }))))) :
  /*#__PURE__*/
  // Grid view for "ALL-TIME FAVORITES"
  React.createElement("div", {
    className: "grid md:grid-cols-1 lg:grid-cols-2 ".concat(shoppingCartTitle ? 'xl:[grid-template-columns:repeat(3,29rem)]' : 'xl:grid-cols-3 max-w-[119rem] mx-auto gap-y-[6rem]', " gap-6 justify-start")
  }, products.map(product => {
    var _product$customizatio2, _product$category4, _product$category5, _product$category6;
    var priceText = product.price && product.price.regular ? product.price.regular.value : '';
    var imageUrl = product.image ? product.image.single : '';
    var isCustomizable = !!((_product$customizatio2 = product.customizationGroups) !== null && _product$customizatio2 !== void 0 && _product$customizatio2.length);
    return /*#__PURE__*/React.createElement(ProductPicks, {
      storeStatus: storeStatus,
      key: product.ProductId,
      image: imageUrl,
      category: isAr ? ((_product$category4 = product.category) === null || _product$category4 === void 0 ? void 0 : _product$category4.nameAr) || ((_product$category5 = product.category) === null || _product$category5 === void 0 ? void 0 : _product$category5.name) : (_product$category6 = product.category) === null || _product$category6 === void 0 ? void 0 : _product$category6.name,
      name: isAr ? product.nameAr || product.name : product.name,
      isSpicy: product.isSpicy
      // isNewItem={product?.new_item}
      ,
      description: extractDescriptionText(isAr ? product.descriptionAr || product.description : product.description),
      price: priceText,
      url: product.url || "/product/".concat(product.url_key),
      stockAvailability: product.stock_status !== 'OUT_OF_STOCK',
      onAddToCart: () => handleButtonClick(product),
      shoppingCartTitle: shoppingCartTitle,
      isCustomizable: isCustomizable
    });
  })), /*#__PURE__*/React.createElement(ProductModal, {
    isOpen: isOpen,
    product: modalProduct,
    onClose: closeModal,
    storeStatus: storeStatus
  }));
}